import { useTranslation } from 'react-i18next';
import { RegexExpressions } from 'shared/constants/regex/regex-expressions';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import { useValidateDeviceDetectionLoopNumber } from 'features/device/domain/queries/deso-queries';
import { useValidateBLEBeacon, useValidateDeviceExternalId, useValidateDeviceName } from 'features/device/domain/queries/device-queries';
import { DeviceFunction } from 'features/device/models/device-function';

interface DeviceValidationHook {
  validateName: () => Promise<boolean | string>;
  validateExternalId: () => Promise<boolean | string>;
  validateDetectionLoopNumber: () => Promise<boolean | string>;
  validateBeacon: () => Promise<boolean | string>;
}

const useDeviceValidation = (): DeviceValidationHook => {
  const { t } = useTranslation('deviceDetails');
  const { currentDevice, currentViewingMode, form } = useDeviceDetailsHook();

  const { mutate: validateDeviceDetectionLoopNumberMutation } = useValidateDeviceDetectionLoopNumber();
  const { mutate: validateDeviceNameMutation } = useValidateDeviceName();
  const { mutate: validateDeviceExternalIdMutation } = useValidateDeviceExternalId();
  const { mutate: validateBLEBeaconMutation } = useValidateBLEBeacon();

  const isLinkedToSystem = currentDevice?.system != null;

  const validateName = async (): Promise<boolean | string> => {
    if (currentDevice?.importDate) return true;

    const name = form.getValues('name')?.trim();
    if (!name) return true;

    const id = form.getValues('id');
    const gatewayId = isLinkedToSystem ? currentDevice.system?.gatewayId : form.getValues('gatewayId');

    return new Promise<boolean | string>((resolve) => {
      validateDeviceNameMutation(
        {
          id: currentViewingMode === 'editing' ? id : undefined,
          deviceName: name,
          gatewayId: gatewayId !== '' ? gatewayId : undefined,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(t('info.uniqueNameHelperText'));
          },
        },
      );
    });
  };

  const validateExternalId = async (): Promise<boolean | string> => {
    const externalId = form.getValues('externalId')?.trim();
    if (!externalId) return true;

    const id = form.getValues('id');

    return new Promise<boolean | string>((resolve) => {
      validateDeviceExternalIdMutation(
        {
          id: currentViewingMode === 'editing' ? id : undefined,
          externalId: currentViewingMode === 'editing' || currentViewingMode === 'creation' ? externalId : undefined,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(t('info.uniqueExternalIdHelperText'));
          },
        },
      );
    });
  };

  const validateDetectionLoopNumber = async (): Promise<boolean | string> => {
    const detectionLoopNumber = form.getValues('detectionLoopNumber');
    const id = form.getValues('id');
    const systemId = form.getValues('systemId');

    if (detectionLoopNumber === undefined || !systemId || systemId === '') return true;

    if (detectionLoopNumber < 1 || detectionLoopNumber > 255) {
      return t('info.detectionLoopNumberOutOfRangeHelperText');
    }

    return new Promise<boolean | string>((resolve) => {
      validateDeviceDetectionLoopNumberMutation(
        {
          id: currentViewingMode === 'editing' ? id : undefined,
          detectionLoopNumber: detectionLoopNumber,
          systemId: systemId !== '' ? systemId : undefined,
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(t('info.uniqueDetectionLoopNumberHelperText'));
          },
        },
      );
    });
  };

  const validateBeacon = async (): Promise<boolean | string> => {
    if (currentDevice?.importDate) return true;

    const proximityId = form.getValues('deviceProximityId');
    if (!proximityId) return true;

    const regex = new RegExp(RegexExpressions.GUID);
    if (!regex.test(proximityId)) return t('BLEBeacon.invalidGuid');

    const functionValue = form.getValues('function');
    return new Promise<boolean | string>((resolve) => {
      validateBLEBeaconMutation(
        {
          deviceId: currentDevice?.id,
          deviceProximityId: proximityId,
          function: DeviceFunction[functionValue as keyof typeof DeviceFunction],
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(t('info.invalidDevice'));
          },
        },
      );
    });
  };

  return {
    validateName,
    validateExternalId,
    validateDetectionLoopNumber,
    validateBeacon,
  };
};

export default useDeviceValidation;
